import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Trophy, GraduationCap, Users, ThumbsUp, BarChart3, ArrowRight, BookOpen } from "lucide-react";

const GOLD = { backgroundImage: "linear-gradient(180deg,#fff3b0,#f5c542 45%,#c98a0b)", WebkitBackgroundClip: "text", backgroundClip: "text" };

const CHECKS = [
  { ms: ["Terbukti", "Berkesan"], en: ["Proven", "Effective"] },
  { ms: ["Cikgu ajar ikut", "tahap pelajar"], en: ["Teachers follow", "each student's level"] },
  { ms: ["Teknik yang sangat", "praktikal 100%"], en: ["A technique that is", "100% practical"] },
  { ms: ["Ada modul", "membaca sendiri"], en: ["Our own", "reading modules"] },
  { ms: ["Fokus", "individual"], en: ["Individual", "focus"] },
  { ms: ["Teknik mengeja pelajar", "cepat lancar membaca"], en: ["Spelling technique for", "faster fluent reading"] },
];
const VALUES = [
  { icon: GraduationCap, ms: "Ilmu", en: "Knowledge" }, { icon: Users, ms: "Keyakinan", en: "Confidence" },
  { icon: ThumbsUp, ms: "Masa Depan", en: "Future" }, { icon: BarChart3, ms: "Kejayaan", en: "Success" },
];

function useCountUp(target, duration = 1800) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let raf;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const step = (t) => {
        const p = Math.min(1, (t - t0) / duration);
        setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [target, duration]);
  return [ref, val];
}

export default function SuccessSection({ ms, setLightbox }) {
  const L = (m, e) => (ms ? m : e);
  const [countRef, count] = useCountUp(20000);
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800" data-testid="landing-success-section">
      <div className="pointer-events-none absolute -left-24 top-20 h-80 w-80 rounded-full bg-orange-500/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-20 h-80 w-80 rounded-full bg-yellow-400/20 blur-3xl" />
      <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-orange-600 via-yellow-300 to-orange-600" />
      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="text-center">
          <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Program</p>
          <h2 className="font-display text-5xl font-extrabold uppercase leading-none tracking-tight text-transparent sm:text-7xl" style={GOLD}>{L("Membaca PMM", "PMM Reading")}</h2>
          <div className="mx-auto mt-5 inline-block -rotate-1 rounded-2xl border-2 border-yellow-400 bg-gradient-to-r from-orange-700 via-orange-500 to-orange-700 px-6 py-3 shadow-2xl shadow-orange-700/40">
            <p className="font-display text-sm font-extrabold uppercase tracking-wide text-white sm:text-lg">{L("Sejak tahun 2007 telah bantu lebih", "Since 2007 we have helped more than")}</p>
            <p ref={countRef} className="num font-display text-4xl font-extrabold uppercase leading-none text-transparent sm:text-6xl" style={GOLD} data-testid="success-count">
              {count.toLocaleString("en-US")} {L("Pelajar", "Students")}
            </p>
            <p className="font-display text-base font-extrabold uppercase tracking-wide text-white sm:text-xl">{L("Lancar Membaca!!", "Read Fluently!!")}</p>
          </div>
        </div>

        <div className="mt-12 grid items-center gap-10 lg:grid-cols-2">
          <div className="relative">
            <button type="button" onClick={() => setLightbox("/landing/pmm/poster-20000.jpg")} className="pmm-cinematic block w-full overflow-hidden rounded-[2rem] border-4 border-white shadow-2xl ring-4 ring-yellow-400/60" data-testid="success-collage">
              <img src="/landing/pmm/success-collage.jpg" alt={L("Pelajar PMM yang telah lancar membaca bersama modul PMM", "PMM students who now read fluently with the PMM module")} className="h-auto w-full transition-transform duration-700 hover:scale-105" loading="lazy" />
            </button>
            <div className="pmm-pop absolute -bottom-8 -left-3 flex flex-col items-center sm:-left-8">
              <div className="grid h-28 w-28 place-items-center rounded-full border-4 border-yellow-400 bg-gradient-to-b from-yellow-200 to-yellow-500 text-center shadow-2xl">
                <span className="font-display text-[11px] font-extrabold uppercase leading-tight text-navy-950"><Trophy className="mx-auto mb-0.5 h-6 w-6" />20,000+<br />{L("Pelajar Lancar", "Fluent")}<br />{L("Membaca", "Readers")}</span>
              </div>
            </div>
          </div>

          <div>
            <div className="grid gap-3 rounded-[2rem] bg-white p-5 shadow-2xl sm:grid-cols-2 sm:p-6" data-testid="success-checks">
              {CHECKS.map((c, i) => (
                <div key={c.en[0]} className="pmm-pop flex items-center gap-3" style={{ animationDelay: `${i * 0.08}s` }}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-orange-500 to-orange-700 text-white shadow-md"><CheckCircle2 className="h-5 w-5" /></span>
                  <span className="font-display text-xs font-extrabold uppercase leading-tight text-navy-900 sm:text-sm">
                    {ms ? c.ms[0] : c.en[0]}<br /><span className="text-orange-600">{ms ? c.ms[1] : c.en[1]}</span>
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
              <Link to="/register" className="-rotate-2 rounded-2xl bg-gradient-to-r from-orange-700 to-orange-500 px-8 py-4 text-center font-display text-2xl font-extrabold uppercase leading-none text-yellow-300 [word-spacing:0.2em] shadow-2xl shadow-orange-700/40 ring-4 ring-white transition-transform duration-300 hover:rotate-0 hover:scale-105" data-testid="success-cta">
                {L("Anak Anda", "Your Child")}<br /><span className="text-white">{L("Boleh Juga!", "Can Too!")}</span>
                <ArrowRight className="ml-2 inline h-5 w-5 text-white" />
              </Link>
              <div className="text-center sm:text-right">
                <BookOpen className="mx-auto h-8 w-8 text-yellow-300 sm:ml-auto sm:mr-0" />
                <p className="font-display text-xs font-extrabold uppercase tracking-widest text-white">{L("Membaca Buka Dunia", "Reading Opens The World")}</p>
                <p className="font-script text-xl text-yellow-300">{L("Bersama Membentuk Generasi Celik Membaca", "Together Shaping A Literate Generation")}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t border-white/10 pt-6">
          {VALUES.map((v) => (
            <span key={v.en} className="flex items-center gap-2 font-display text-xs font-extrabold uppercase tracking-widest text-white sm:text-sm">
              <v.icon className="h-5 w-5 text-yellow-300" /> {ms ? v.ms : v.en}
            </span>
          ))}
          <span className="rounded-full border border-yellow-400/60 px-3 py-1 font-display text-[10px] font-bold uppercase tracking-widest text-yellow-300">PMM • {L("Sejak 2007", "Since 2007")}</span>
        </div>
      </div>
    </section>
  );
}
